'use client';

import type React from 'react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { AlertCircle, PlusCircle } from 'lucide-react';
import { Alert, AlertDescription } from 'components/ui/alert';
import { useTranslation } from 'react-i18next';
import { initialInfoSchema } from './lib/schema';
import firebase from '../../firebase';
import firebaseClass from 'firebase/compat/app';
import { toast } from 'sonner';
import uuid from 'uuid';
import { useAudit } from './audit-context';

export interface InitialInfo {
  title: string;
}

export function AddSubTask({
  auditId,
  taskId,
  taskLabel,
}: {
  auditId: string;
  taskId: string;
  taskLabel: string;
}) {
  const { audit } = useAudit();

  const isDisabled = audit.state === 'COMPLETED';
  const { t } = useTranslation();

  const [formData, setFormData] = useState<InitialInfo>({
    title: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleChange = (field: keyof InitialInfo, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validateForm = () => {
    try {
      initialInfoSchema.parse(formData);
      setOpen(false);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error) {
        // Handle Zod validation errors
        if (error.name === 'ZodError') {
          const zodError = error as unknown as { errors: { path: string[]; message: string }[] };
          const newErrors: Record<string, string> = {};

          zodError.errors.forEach((err) => {
            const field = err.path[0] as string;
            newErrors[field] = err.message;
          });

          setErrors(newErrors);
        }
      }
      return false;
    }
  };

  async function addSubtaskToAudit(formData: InitialInfo) {
    // console.log({ formData });
    try {
      if (!auditId || !taskId) {
        throw new Error(`Missing auditId=${auditId} or taskId=${taskId}`);
      }
      const subTaskId = uuid();
      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`tasksMap.${taskId}.subTaskIds`]:
            firebaseClass.firestore.FieldValue.arrayUnion(subTaskId),

          [`subTasksMap.${subTaskId}`]: {
            id: subTaskId,
            description: '',
            status: 'not-audited',
            taskId: taskId,
            label: formData.title,
          },
        });

      toast.success('Beskrivelse opdateret');
      setFormData({
        title: '',
      });
    } catch (error) {
      toast.error('Fejl ved opdatering af beskrivelse');
      console.error(error);
    }
    // id;
    // '6'(string);
    // label;
    // 'Har medarbejdere den fornødne uddannelse? Tjekkes Site Manager er forpligtet til at afhjælpe afvigelser og observationer inden aftalt deadline. Som udgangspunkt 1 måned efter dato for auditrapport fx kørekort?'(
    //   string
    // );
    // status;
    // 'not-audited'(string);
    // taskId;
    // ('task4');
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      addSubtaskToAudit(formData);
      // update blocks and quantities
    }

    setIsSubmitting(false);
  };

  // Check if there are any validation errors
  const hasErrors = Object.values(errors).some((error) => error !== '');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="w-full" disabled={isDisabled}>
          <PlusCircle strokeWidth={1.5} size={18} /> Tilføj punkt til {taskLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Dokumentinformation</DialogTitle>
        </DialogHeader>

        {hasErrors && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Ret venligst fejlene nedenfor, før du fortsætter.</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="flex items-center">
                Title
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Indtast title"
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Tilføjer...' : 'Tilføj'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
